"use strict";Object.defineProperty(exports, "__esModule", {value: true});var _react = require('react');
var _typed = require('shared/hooks/typed');




var _user = require('shared/selectors/user');
var _peers = require('shared/selectors/peers');
var _smsDelivery = require('shared/services/smsDelivery');

const EMPTY_MESSAGES = []

// Message keys we have already raised a notice for. Module-level on purpose so
// remounting a thread does not replay recent toasts. Retention and size are
// bounded for long-lived staff sessions; old history still cannot notify
// because it fails the fresh-send/transition checks below.
const notifiedMessageKeys = new Map()
const NOTIFIED_MESSAGE_RETENTION_MS = 60 * 60 * 1000
const MAX_NOTIFIED_MESSAGE_KEYS = 1000

// A message first seen already rejected counts as "just sent" only inside
// this window; anything older at first sight is history and never notifies.
const FRESH_SEND_WINDOW_MS = 30 * 1000

 const __resetSMSRejectionNoticesForTests = () => notifiedMessageKeys.clear(); exports.__resetSMSRejectionNoticesForTests = __resetSMSRejectionNoticesForTests

const pruneNotifiedMessageKeys = (now) => {
  const cutoff = now - NOTIFIED_MESSAGE_RETENTION_MS
  notifiedMessageKeys.forEach((notifiedAt, key) => {
    if (notifiedAt < cutoff) notifiedMessageKeys.delete(key)
  })
  while (notifiedMessageKeys.size >= MAX_NOTIFIED_MESSAGE_KEYS) {
    const oldest = notifiedMessageKeys.keys().next().value
    if (!oldest) break
    notifiedMessageKeys.delete(oldest)
  }
}

/**
 * Raises `notify(text)` exactly once per message whose text was rejected for
 * unsubscribed recipients. Staff keep sending normally; the backend records
 * the rejection on the message and republishes it, and this hook turns that
 * transition into the one-time warning.
 *
 * A message counts as "newly rejected" when either:
 *   - we previously observed it without a rejection and it now has one
 *     (the normal optimistic-send → republished-update path), or
 *   - it is first seen already rejected but was sent by the current user
 *     after this hook mounted (the two updates landed in one render).
 * Messages loaded already-rejected from history never notify.
 */
 const useSMSRejectionNotices = (
  conversationKey,
  notify,
) => {
  const messages = _typed.useAppSelector.call(void 0, (state) =>
    (conversationKey && state.dataV2.messages[conversationKey]) || EMPTY_MESSAGES)
  const currentUserKey = _typed.useAppSelector.call(void 0, _user.selectCurrentUserKey)
  const patientsV1 = _typed.useAppSelector.call(void 0, _peers.selectAllV1Patients)
  const patientsV2 = _typed.useAppSelector.call(void 0, _peers.selectAllV2Patients)

  const observedRejected = _react.useRef(new Map())
  const mountedAt = _react.useRef(0)
  const notifyRef = _react.useRef.call(void 0, notify)

  _react.useEffect.call(void 0, () => {
    notifyRef.current = notify
  }, [notify])

  _react.useEffect.call(void 0, 
    () => {
      observedRejected.current = new Map()
      mountedAt.current = Date.now()
    },
    [conversationKey],
  )

  _react.useEffect.call(void 0, 
    () => {
      const observed = observedRejected.current
      const now = Date.now()
      pruneNotifiedMessageKeys(now)
      messages.forEach(message => {
        const summary = _smsDelivery.summarizeSMSOutcomes.call(void 0, message.SMSDeliveryOutcomes)
        const isRejected = !!summary
        const seenBefore = observed.has(message.MessageKey)
        const wasRejected = observed.get(message.MessageKey) === true
        observed.set(message.MessageKey, isRejected)

        if (!summary || wasRejected || notifiedMessageKeys.has(message.MessageKey)) return
        // Only the sender is warned, whichever way the rejection arrives.
        if (message.UserKey !== currentUserKey) return

        const transitioned = seenBefore && !wasRejected
        const sentAt = Date.parse(message.SentAt)
        const freshOwnSend = !seenBefore &&
          Number.isFinite(sentAt) &&
          sentAt >= mountedAt.current - FRESH_SEND_WINDOW_MS
        if (!transitioned && !freshOwnSend) return

        notifiedMessageKeys.set(message.MessageKey, now)
        notifyRef.current(_smsDelivery.makeSMSRejectionNotice.call(void 0, summary, { ...patientsV1, ...patientsV2 }))
      })
    },
    [messages, currentUserKey, patientsV1, patientsV2],
  )
}; exports.useSMSRejectionNotices = useSMSRejectionNotices

exports. default = exports.useSMSRejectionNotices
